import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { COLORS, SHADOWS } from '../../styles/tokens';
import { useIsMobile } from '../../hooks/useIsMobile';

const CHECKLIST = [
  {
    category: 'Juridisk',
    items: [
      { id: 'privacy_policy',     label: 'Personvernerklæring publisert på /personvern' },
      { id: 'dpa_google',         label: 'Databehandleravtale med Google signert (Firebase + Maps)' },
      { id: 'dpa_stripe',         label: 'Databehandleravtale med Stripe signert' },
      { id: 'dpa_creditsafe',     label: 'Databehandleravtale med Bisnode/Creditsafe signert' },
      { id: 'terms_driver',       label: 'Brukeravtale for sjåfør (B2B, selvstendig næringsdrivende) klar' },
      { id: 'terms_orderer',      label: 'Brukeravtale for bestiller klar' },
      { id: 'freight_validated',  label: 'Fraktbrev-utforming bekreftet av bransjonskontakt (Vegfraktloven)' },
    ],
  },
  {
    category: 'GDPR',
    items: [
      { id: 'location_consent',   label: 'Lokasjonskonsent-modal implementert og testet for sjåfører' },
      { id: 'delete_routine',     label: 'Sletterutine: persondata slettes innen 30 dager ved avsluttet konto' },
      { id: 'cookie_banner',      label: 'Cookie-/sporingsinfo vist ved første besøk (kun hvis analytics brukes)' },
    ],
  },
  {
    category: 'Teknisk',
    items: [
      { id: 'firebase_hosting',   label: 'Firebase Hosting deployet (react-app tilgjengelig online)' },
      { id: 'firestore_rules',    label: 'Firestore Security Rules deployet og testet' },
      { id: 'storage_rules',      label: 'Firebase Storage Rules deployet' },
      { id: 'firestore_indexes',  label: 'Firestore-indekser deployet (ingen missing-index-feil i console)' },
      { id: 'cloud_functions',    label: 'Cloud Functions deployet (Stripe-integrasjon aktiv)' },
      { id: 'stripe_secret',      label: 'STRIPE_SECRET_KEY satt i Firebase Secret Manager' },
      { id: 'stripe_live',        label: 'Stripe byttet til live-modus (pk_live_ / sk_live_)' },
      { id: 'maps_key_restricted','label': 'Google Maps API-nøkkel begrenset til ditt domene' },
      { id: 'auth_domains',       label: 'Firebase Auth: authorized domains oppdatert med produksjons-URL' },
      { id: 'env_production',     label: '.env.production satt med korrekte produksjonsverdier' },
    ],
  },
  {
    category: 'Testing',
    items: [
      { id: 'test_register_driver',   label: 'Testregistrering sjåfør (inkl. org.nr. + kjøretøy)' },
      { id: 'test_register_orderer',  label: 'Testregistrering bestiller (inkl. org.nr. + betalingskort)' },
      { id: 'test_create_order',      label: 'Opprett og publiser testoppdrag (fastpris + anbud)' },
      { id: 'test_bid_accept',        label: 'Sjåfør byr — bestiller aksepterer — stripe autoriserer' },
      { id: 'test_pickup',            label: 'Sjåfør registrerer henting (foto + vekt)' },
      { id: 'test_weight_deviation',  label: 'Vektavvik > 5% testet — bestiller varsles og godkjenner' },
      { id: 'test_delivery',          label: 'Sjåfør registrerer levering (foto + signatur)' },
      { id: 'test_invoice',           label: 'Faktura generert automatisk etter levering' },
      { id: 'test_dispute',           label: 'Tvist opprettet og behandlet av admin' },
      { id: 'test_earnings',          label: 'Sjåfør ser inntekter korrekt på /driver/earnings' },
    ],
  },
];


export default function PreLaunchChecklist() {
  const [checked, setChecked] = useState({});
  const [saving,  setSaving]  = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    getDoc(doc(db, 'settings', 'prelaunch_checklist')).then(snap => {
      if (snap.exists()) setChecked(snap.data().items ?? {});
    });
  }, []);

  async function toggle(id) {
    const newChecked = { ...checked, [id]: !checked[id] };
    setChecked(newChecked);
    setSaving(true);
    await setDoc(doc(db, 'settings', 'prelaunch_checklist'), { items: newChecked });
    setSaving(false);
  }

  const allItems  = CHECKLIST.flatMap(c => c.items);
  const doneCount = allItems.filter(i => checked[i.id]).length;
  const pct       = Math.round((doneCount / allItems.length) * 100);

  return (
    <div style={{ ...styles.page, ...(isMobile ? { padding: 16 } : {}) }}>
      <div style={styles.header}>
        <h2 style={styles.title}>Pre-launch sjekkliste</h2>
        <div style={styles.progress}>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: `${pct}%`, background: pct === 100 ? COLORS.success : COLORS.primary }} />
          </div>
          <span style={styles.progressLabel}>{doneCount}/{allItems.length} ({pct}%)</span>
          {saving && <span style={{ fontSize: 12, color: COLORS.textLight, marginLeft: 8 }}>Lagrer…</span>}
        </div>
      </div>

      {pct === 100 && (
        <div style={styles.readyBanner}>
          🚀 Alle punkter er krysset av — klar for pilotlansering!
        </div>
      )}

      {CHECKLIST.map(category => (
        <div key={category.category} style={styles.card}>
          <h3 style={styles.categoryTitle}>{category.category}</h3>
          {category.items.map(item => (
            <label key={item.id} style={styles.checkRow}>
              <input
                type="checkbox"
                checked={!!checked[item.id]}
                onChange={() => toggle(item.id)}
                style={{ marginRight: 12, width: 16, height: 16, cursor: 'pointer', accentColor: COLORS.primary }}
              />
              <span style={{ color: checked[item.id] ? COLORS.textLight : COLORS.textDark, textDecoration: checked[item.id] ? 'line-through' : 'none', fontSize: 14 }}>
                {item.label}
              </span>
            </label>
          ))}
        </div>
      ))}
    </div>
  );
}

const styles = {
  page:          { padding: '24px 28px', overflowY: 'auto', height: '100%' },
  header:        { marginBottom: 20 },
  title:         { fontSize: 20, fontWeight: 700, color: COLORS.textDark, margin: '0 0 12px' },
  progress:      { display: 'flex', alignItems: 'center', gap: 12 },
  progressBar:   { flex: 1, height: 8, background: COLORS.border, borderRadius: 4, overflow: 'hidden', maxWidth: 300 },
  progressFill:  { height: '100%', borderRadius: 4, transition: 'width 0.3s, background 0.3s' },
  progressLabel: { fontSize: 14, fontWeight: 600, color: COLORS.textMid },
  readyBanner:   { background: COLORS.successBg, border: `1px solid ${COLORS.successBorder}`, borderRadius: 10, padding: '14px 20px', marginBottom: 16, fontSize: 15, fontWeight: 600, color: COLORS.successText },
  card:          { background: COLORS.white, borderRadius: 10, padding: '20px 24px', marginBottom: 14, boxShadow: SHADOWS.sm },
  categoryTitle: { fontSize: 13, fontWeight: 700, color: COLORS.textLight, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 14px' },
  checkRow:      { display: 'flex', alignItems: 'flex-start', padding: '6px 0', cursor: 'pointer', borderBottom: `1px solid ${COLORS.border}` },
};
